import { io, Socket } from 'socket.io-client';
import { ref } from 'vue';
import { useMessageStore } from '../store/messages';
import { useAuthStore } from '../store/auth';
import { config } from '../utils/config';
import type { MessageStore } from '../store/messages';
import type { AuthStore } from '../store/auth';

class SocketService {
  private socket: Socket | null = null;
  private messageStore: MessageStore | null = null;
  private authStore: AuthStore | null = null;
  public isConnected = ref(false);
  public botIsTyping = ref(false);
  public error = ref<Error | null>(null);
  public connecting = ref(false);

  private getStores() {
    if (!this.messageStore) {
      this.messageStore = useMessageStore();
    }
    if (!this.authStore) {
      this.authStore = useAuthStore();
    }
    return {
      messageStore: this.messageStore,
      authStore: this.authStore,
    };
  }

  connect(conversationId?: string) {
    if (this.socket?.connected || this.connecting.value) {
      return;
    }

    this.connecting.value = true;
    this.error.value = null;
    const { authStore } = this.getStores();
    const token = authStore.token;

    try {
      this.socket = io(`${config.apiUrl}/webchat`, {
        auth: token ? { token } : undefined,
        query: conversationId ? { conversationId } : undefined,
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      this.setupEventListeners();
    } catch (err) {
      this.error.value = err instanceof Error ? err : new Error('Failed to connect to chat server');
      this.connecting.value = false;
      console.error('[Orbitar Chat] Socket initialization error:', err);
    }
  }

  private setupEventListeners() {
    if (!this.socket) return;

    const { messageStore, authStore } = this.getStores();

    this.socket.on('connect', () => {
      this.isConnected.value = true;
      this.connecting.value = false;
      this.error.value = null;
      console.log('[Orbitar Chat] Connected to WebSocket server');
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected.value = false;
      console.log('[Orbitar Chat] Disconnected from WebSocket server:', reason);
      
      // Add system message for unexpected disconnects
      if (reason !== 'io client disconnect') {
        messageStore.addSystemMessage('Disconnected from server. Trying to reconnect...');
      }
    });

    this.socket.on('connect_error', (error) => {
      this.error.value = error;
      this.connecting.value = false;
      console.error('[Orbitar Chat] Connection error:', error);
      messageStore.addSystemMessage('Connection error. Trying to reconnect...');
    });

    this.socket.on('reconnect', (attemptNumber) => {
      this.isConnected.value = true;
      this.error.value = null;
      console.log(`[Orbitar Chat] Reconnected after ${attemptNumber} attempts`);
      messageStore.addSystemMessage('Reconnected to server!');
    });

    this.socket.on('reconnect_error', (error) => {
      this.error.value = error;
      console.error('[Orbitar Chat] Reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      this.connecting.value = false;
      this.error.value = new Error('Failed to reconnect after multiple attempts');
      console.error('[Orbitar Chat] Reconnection failed');
      messageStore.addSystemMessage('Failed to reconnect. Please refresh the page.');
    });

    this.socket.on('new_message', (data: any) => {
      const { message, sender } = data;
      messageStore.addMessage({
        id: message.id,
        content: message.content,
        type: message.type,
        senderType: sender === 'agent' ? 'bot' : sender === 'ai' ? 'bot' : 'user',
        timestamp: new Date(message.createdAt || Date.now()).getTime(),
        status: message.status || 'delivered',
        metadata: message.metadata,
      });
    });

    this.socket.on('typing_status', (data: any) => {
      if (data.userId !== authStore.userId) {
        this.botIsTyping.value = data.isTyping;
      }
    });

    this.socket.on('status_updated', (data: any) => {
      console.log('[Orbitar Chat] Conversation status updated:', data);
    });

    this.socket.on('system_message', (data: any) => {
      if (data.content) {
        messageStore.addSystemMessage(data.content);
      }
    });
  }

  joinConversation(conversationId: string | null, widgetId?: string, visitorInfo?: any): Promise<string> {
    if (!this.socket?.connected) {
      this.connect();
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for socket connection'));
        }, 5000);
        
        const connectListener = () => {
          clearTimeout(timeout);
          this.socket?.off('connect', connectListener);
          this.joinConversation(conversationId, widgetId, visitorInfo)
            .then(resolve)
            .catch(reject);
        };
        
        this.socket?.on('connect', connectListener);
      });
    }

    const { messageStore } = this.getStores();

    return new Promise<string>((resolve, reject) => {
      const payload: any = {};
      
      // If we have a conversationId, use it
      if (conversationId) {
        payload.conversationId = conversationId;
      }
      
      // If no conversationId but widgetId is provided, use that to create a new conversation
      if (widgetId) {
        payload.widgetId = widgetId;
      }
      
      // Include visitor info if provided
      if (visitorInfo) {
        payload.visitorId = visitorInfo.visitorId;
        payload.visitorInfo = visitorInfo;
      }
      
      this.socket?.emit('join_conversation', payload, (response: any) => {
        if (response?.success && response?.conversationId) {
          resolve(response.conversationId as string);
        } else {
          console.error('[Orbitar Chat] Failed to join conversation:', response?.error);
          messageStore.addSystemMessage('Failed to join conversation');
          reject(new Error(response?.error || 'Failed to join conversation'));
        }
      });
    });
  }

  leaveConversation() {
    if (!this.socket?.connected) return Promise.resolve();

    return new Promise((resolve, reject) => {
      this.socket?.emit('leave_conversation', (response: any) => {
        if (response?.success) {
          resolve(response);
        } else {
          console.error('[Orbitar Chat] Failed to leave conversation:', response?.error);
          reject(new Error(response?.error || 'Failed to leave conversation'));
        }
      });
    });
  }

  sendMessage(conversationId: string, content: string, metadata?: Record<string, any>) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      this.socket.emit('send_message', { conversationId, content, metadata }, (response: any) => {
        if (response?.success) {
          resolve(response.messageId);
        } else {
          reject(new Error(response?.error || 'Failed to send message'));
        }
      });
    });
  }

  sendTypingStatus(conversationId: string, isTyping: boolean) {
    if (!this.socket?.connected) return;

    this.socket.emit('typing', { conversationId, isTyping });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected.value = false;
      this.connecting.value = false;
      this.error.value = null;
    }
  }
}

export const socketService = new SocketService(); 